import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "../../api";
import { useApi } from "../../hooks";
import { shortWhen } from "../../crm";
import { ENROLLMENT_LABEL, progressText, type ContactEnrollment } from "../../drips";
import "../../polish/crm-drips.css";

type ForContact = {
  enrollments: ContactEnrollment[];
  live_drips: { id: number; name: string }[];
  unsubscribed_at: string | null;
  has_email: boolean;
};

/**
 * A contact's drips, for the contact drawer: each drip they are or were in, its
 * status and next email (Eastern), Stop, and Add to drip (live drips only).
 */
export default function ContactDrips({ contactId, canWrite }: { contactId: number; canWrite: boolean }) {
  const data = useApi<ForContact>(`/api/crm/drips/for-contact/${contactId}`);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const d = data.data;
  if (!d) return null;
  const inActive = new Set(d.enrollments.filter((e) => e.status === "active").map((e) => e.drip_id));
  const choices = d.live_drips.filter((x) => !inActive.has(x.id));
  const canAdd = canWrite && d.has_email && !d.unsubscribed_at && choices.length > 0;
  if (!d.enrollments.length && !canAdd && !d.unsubscribed_at) return null;

  async function add() {
    if (!pick) return;
    setBusy(true); setError("");
    try {
      await api(`/api/crm/drips/for-contact/${contactId}`, { method: "POST", body: JSON.stringify({ drip_id: Number(pick) }) });
      setPick(""); data.reload();
    } catch (e) { setError(e instanceof Error ? e.message : "That did not work."); }
    finally { setBusy(false); }
  }
  async function stop(e: ContactEnrollment) {
    setBusy(true); setError("");
    try { await api(`/api/crm/drips/${e.drip_id}/people/${e.id}/stop`, { method: "POST" }); data.reload(); }
    catch (err) { setError(err instanceof Error ? err.message : "That did not work."); }
    finally { setBusy(false); }
  }

  return (
    <section className="crm-section crm-drips" aria-label="Drips">
      <h3>Drips</h3>
      {d.unsubscribed_at && <p className="crm-empty">Unsubscribed from drip emails on {formatDate(d.unsubscribed_at)}.</p>}
      {d.enrollments.length > 0 && (
        <ul className="crm-drip-list">
          {d.enrollments.map((e) => (
            <li key={e.id}>
              <span className="crm-drip-list-main">
                <Link to={`/crm/drips?drip=${e.drip_id}&tab=people`}>{e.name}</Link>
                <small>
                  {e.status === "active" && e.next_send_at
                    ? `Email ${e.next_position + 1} of ${e.steps} on ${shortWhen(e.next_send_at)}${e.drip_status !== "live" ? " (drip paused)" : ""}`
                    : e.status === "finished" ? progressText(e.steps, e.steps) : e.stop_reason || ENROLLMENT_LABEL[e.status]}
                </small>
              </span>
              <span className={`crm-status crm-enroll-status ${e.status}`}>{ENROLLMENT_LABEL[e.status]}</span>
              {canWrite && e.status === "active" && <button type="button" className="button secondary small" disabled={busy} onClick={() => void stop(e)}>Stop</button>}
            </li>
          ))}
        </ul>
      )}
      {canAdd && (
        <div className="crm-drip-addrow">
          <select aria-label="Drip to add them to" value={pick} disabled={busy} onChange={(e) => setPick(e.target.value)}>
            <option value="">Add to a drip…</option>
            {choices.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <button type="button" className="button secondary small" disabled={busy || !pick} onClick={() => void add()}>Add</button>
        </div>
      )}
      {error && <p className="crm-error" role="alert">{error}</p>}
    </section>
  );
}

export { ContactDrips };
